/**
 * i18n core — isolated, additive internationalisation layer.
 *
 *  - `locale`       writable store holding the active locale code ('en', 'zh-TW', 'zh-CN', 'ja')
 *  - `t`            derived store: use `{$t('some.key')}` / `{$t('key', {n: 3})}` in components
 *  - `tr(key)`      plain function for non-component JS (reads the current locale, not reactive)
 *  - `setLocale()`  switch language (persisted in localStorage, cookie fallback)
 *  - `suggestion`   store holding the locale suggested on a first visit (from navigator.languages)
 *
 * Missing keys fall back to English, then to the key itself. English is always the default.
 * Existing code only needs `import { t } from '.../i18n'` and `{$t('key')}` where a string is shown.
 */
import { writable, derived, get } from 'svelte/store';
import Cookies from 'js-cookie';
import moment from 'moment';

import en   from './locales/en';
import zhTW from './locales/zh-TW';
import zhCN from './locales/zh-CN';
import ja   from './locales/ja';
import { applyDataTablesLocale } from './datatables';

export const DEFAULT_LOCALE = 'en';

/** Registry of available languages (order = order shown in the switcher). */
export const LOCALES = [
    { code: 'en',    native: 'English',  short: 'EN',   intl: 'en'    },
    { code: 'zh-TW', native: '繁體中文', short: '繁中', intl: 'zh-TW' },
    { code: 'zh-CN', native: '简体中文', short: '简中', intl: 'zh-CN' },
    { code: 'ja',    native: '日本語',   short: 'JA',   intl: 'ja'    },
];

const DICTIONARIES = { 'en': en, 'zh-TW': zhTW, 'zh-CN': zhCN, 'ja': ja };

const STORAGE_LOCALE    = 'i18n.locale';
const STORAGE_DISMISSED = 'i18n.suggest.dismissed';

// ---------------------------------------------------------------------------
// Persistence (localStorage first, cookie as a fallback for restricted contexts)
// ---------------------------------------------------------------------------
function storageGet(key) {
    try { const v = window.localStorage.getItem(key); if (v !== null) return v; } catch (e) { /* ignore */ }
    try { return Cookies.get(key) || null; } catch (e) { return null; }
}
function storageSet(key, value) {
    try { window.localStorage.setItem(key, value); return; } catch (e) { /* ignore */ }
    try { Cookies.set(key, value, { expires: 365 }); } catch (e) { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Translation
// ---------------------------------------------------------------------------
function lookup(dict, key) {
    return String(key).split('.').reduce((o, k) => (o && typeof o === 'object') ? o[k] : undefined, dict);
}
function interpolate(str, params) {
    if (!params) return str;
    return str.replace(/\{(\w+)\}/g, (m, k) => (params[k] !== undefined && params[k] !== null) ? params[k] : m);
}

const warned = {};

/**
 * Translate `key` in locale `code`.
 *  - params.{name} are interpolated: "Day {n}" + {n: 3} -> "Day 3"
 *  - when params.n is a number, `key_one` / `key_other` are tried first (English plurals),
 *    then `key` (CJK languages do not inflect and only define `key`)
 *  - falls back to English, then to the key itself
 */
export function translate(code, key, params) {
    const chain = (code && code !== DEFAULT_LOCALE) ? [code, DEFAULT_LOCALE] : [DEFAULT_LOCALE];
    const n = (params && typeof params.n === 'number') ? params.n : null;
    for (const c of chain) {
        const dict = DICTIONARIES[c]; if (!dict) continue;
        let val;
        if (n !== null) val = lookup(dict, key + (n === 1 ? '_one' : '_other'));
        if (typeof val !== 'string') val = lookup(dict, key);
        if (typeof val === 'string') return interpolate(val, params);
    }
    if (!warned[key]) { warned[key] = true; try { console.warn('[i18n] missing key: ' + key); } catch (e) { /* ignore */ } }
    return key;
}

export const locale = writable(DEFAULT_LOCALE);

/** Derived store: `$t('key', params)` re-renders automatically when the locale changes. */
export const t = derived(locale, code => (key, params) => translate(code, key, params));

/** Plain function for non-component code (not reactive). */
export function tr(key, params) { return translate(get(locale), key, params); }

export function isSupported(code) { return !!DICTIONARIES[code]; }
export function localeInfo(code)  { return LOCALES.find(l => l.code === code) || LOCALES[0]; }

export function setLocale(code, opts) {
    opts = opts || {};
    if (!isSupported(code)) code = DEFAULT_LOCALE;
    locale.set(code);
    if (opts.persist !== false) storageSet(STORAGE_LOCALE, code);
}

// Side effects of a locale change: <html lang> and DataTables strings.
locale.subscribe(code => {
    try { document.documentElement.setAttribute('lang', code); } catch (e) { /* ignore */ }
    applyDataTablesLocale(code);
});

// ---------------------------------------------------------------------------
// Browser language detection / first-visit suggestion
// ---------------------------------------------------------------------------

/** Map a BCP-47 tag ('ja-JP', 'zh-Hant-TW', 'zh', ...) to a supported locale code, or null. */
export function matchLocale(tag) {
    const parts = String(tag || '').toLowerCase().split('-').filter(Boolean);
    if (!parts.length) return null;
    const primary = parts[0], rest = parts.slice(1);
    if (primary === 'en') return 'en';
    if (primary === 'ja') return 'ja';
    if (primary === 'zh') {
        const simplified = ['hans', 'cn', 'sg', 'my'].some(s => rest.includes(s));
        return simplified ? 'zh-CN' : 'zh-TW'; // hant / tw / hk / mo / bare "zh" -> Traditional (primary audience)
    }
    return null;
}

/**
 * Pick the locale to suggest from an ordered preference list (navigator.languages reflects
 * the Accept-Language order, highest quality first). We only suggest a language ranked ABOVE
 * any English entry: a visitor who prefers English gets no prompt at all.
 */
export function pickSuggestion(languages) {
    for (const tag of (languages || [])) {
        const m = matchLocale(tag);
        if (m === DEFAULT_LOCALE) return null;
        if (m) return m;
    }
    return null;
}

export function browserLanguages() {
    if (typeof navigator === 'undefined') return [];
    const list = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language || navigator.userLanguage];
    return Array.from(list).filter(Boolean);
}

/** Locale code currently suggested to the visitor (null when nothing to suggest / already handled). */
export const suggestion = writable(null);

export function dismissSuggestion(remember) {
    suggestion.set(null);
    if (remember !== false) storageSet(STORAGE_DISMISSED, '1');
}
export function acceptSuggestion() {
    const code = get(suggestion);
    if (code) setLocale(code);
    suggestion.set(null);
}

// ---------------------------------------------------------------------------
// Relative time ("3 minutes ago") in the active language, moment-like thresholds
// ---------------------------------------------------------------------------
const rtfCache = {};
function getRtf(code) {
    if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') return null;
    if (!rtfCache[code]) rtfCache[code] = new Intl.RelativeTimeFormat(localeInfo(code).intl, { numeric: 'auto' });
    return rtfCache[code];
}

export function formatRelative(value, code) {
    const m = moment(value);
    if (!m.isValid()) return (value === null || value === undefined) ? '' : String(value);
    let rtf = null;
    try { rtf = getRtf(code || get(locale)); } catch (e) { rtf = null; }
    if (!rtf) return m.fromNow();

    const diff = Math.round((m.valueOf() - Date.now()) / 1000);
    const abs  = Math.abs(diff), sign = diff < 0 ? -1 : 1;
    if (abs < 45)        return rtf.format(0, 'second');
    if (abs < 90)        return rtf.format(sign, 'minute');
    const min = Math.round(abs / 60);
    if (min < 45)        return rtf.format(sign * min, 'minute');
    if (abs < 90 * 60)   return rtf.format(sign, 'hour');
    const hr = Math.round(abs / 3600);
    if (hr < 22)         return rtf.format(sign * hr, 'hour');
    if (abs < 36 * 3600) return rtf.format(sign, 'day');
    const day = Math.round(abs / 86400);
    if (day < 26)        return rtf.format(sign * day, 'day');
    if (day < 45)        return rtf.format(sign, 'month');
    if (day < 320)       return rtf.format(sign * Math.round(day / 30.4), 'month');
    return rtf.format(sign * Math.round(day / 365), 'year');
}

/** Derived store: `{$relativeTime(date)}` re-renders when the locale changes. */
export const relativeTime = derived(locale, code => value => formatRelative(value, code));

/** Plain function for non-component code. */
export function fromNow(value) { return formatRelative(value, get(locale)); }

// ---------------------------------------------------------------------------
// Calendar-day distances ("in 4 days", "in 2 weeks", "in 1 month") and exact dates
// ---------------------------------------------------------------------------
const rtfNumericCache = {};
function getRtfNumeric(code) {
    if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') return null;
    if (!rtfNumericCache[code]) rtfNumericCache[code] = new Intl.RelativeTimeFormat(localeInfo(code).intl, { numeric: 'always' });
    return rtfNumericCache[code];
}

/**
 * Whole-day distance as text: "today", "tomorrow", "in 4 days", "in 1 week", "in 3 months", "5 days ago".
 * `days` is a calendar-day count (negative = past), see Deadlines.daysUntil().
 */
export function formatRelativeDays(days, code) {
    days = Math.round(Number(days) || 0);
    code = code || get(locale);
    let auto = null, always = null;
    try { auto = getRtf(code); always = getRtfNumeric(code); } catch (e) { auto = always = null; }
    if (!auto || !always) return days === 0 ? 'today' : (days > 0 ? 'in ' + days + ' days' : (-days) + ' days ago');
    const abs = Math.abs(days), sign = days < 0 ? -1 : 1;
    if (abs < 7)   return auto.format(days, 'day');                                   // today / tomorrow / in 3 days
    if (abs < 30)  return always.format(sign * Math.max(1, Math.round(abs / 7)), 'week');
    if (abs < 365) return always.format(sign * Math.max(1, Math.round(abs / 30.4)), 'month');
    return always.format(sign * Math.max(1, Math.round(abs / 365)), 'year');
}

/** Derived store: `{$relativeDays(n)}` re-renders when the locale changes. */
export const relativeDays = derived(locale, code => days => formatRelativeDays(days, code));

const dtfCache = {};
/** Exact date in the active language: "October 30, 2026" / "2026年10月30日" / "2026年10月30日". */
export function formatDate(value, code, options) {
    const d = value instanceof Date ? value : moment(value).toDate();
    if (!d || isNaN(d.getTime())) return (value === null || value === undefined) ? '' : String(value);
    code = code || get(locale);
    const opts = options || { year: 'numeric', month: 'long', day: 'numeric' };
    const key = code + '|' + JSON.stringify(opts);
    try {
        if (!dtfCache[key]) dtfCache[key] = new Intl.DateTimeFormat(localeInfo(code).intl, opts);
        return dtfCache[key].format(d);
    } catch (e) {
        return moment(d).format('YYYY-MM-DD');
    }
}

// ---------------------------------------------------------------------------
// Initialisation (runs once when the module is first imported)
// ---------------------------------------------------------------------------
let initialized = false;
export function init() {
    if (initialized) return; initialized = true;

    // 1) explicit ?lang=xx in the URL wins and is remembered (handy for support links)
    let fromQuery = null;
    try { fromQuery = new URLSearchParams(window.location.search).get('lang'); } catch (e) { /* ignore */ }
    const queryLocale = fromQuery ? (isSupported(fromQuery) ? fromQuery : matchLocale(fromQuery)) : null;
    if (queryLocale) { setLocale(queryLocale); return; }

    // 2) a previously chosen language
    const stored = storageGet(STORAGE_LOCALE);
    if (stored && isSupported(stored)) { setLocale(stored, { persist: false }); return; }

    // 3) first visit: English by default, plus a suggestion when the browser clearly prefers
    //    one of our other languages (and the visitor has not dismissed the hint before)
    setLocale(DEFAULT_LOCALE, { persist: false });
    if (storageGet(STORAGE_DISMISSED) === '1') return;
    const suggested = pickSuggestion(browserLanguages());
    if (suggested) suggestion.set(suggested);
}

if (typeof window !== 'undefined') init();
